import type { Mutation } from '@tanstack/react-query'
import { ApiError } from './client'
import { toast } from '@/stores/toast'
import { captureHandledError } from '@/sentry'
import { isManagedRefusal, managedRefusalDetail } from './managed-refusal'

/**
 * The toast the safety net shows. Exported for a caller that reports some
 * failures itself and leaves the rest to the usual toast.
 *
 * A booking the booking system owns (CLA-1695) is not "something went wrong":
 * the server says where to change it, so that sentence is the toast.
 */
export function toastMutationError(err: unknown): void {
  if (isManagedRefusal(err)) {
    toast.error(managedRefusalDetail(err) ?? 'This booking is managed in your booking system.')
    return
  }
  if (err instanceof ApiError) {
    // Don't toast on 401 — handled by auth refresh flow
    if (err.status !== 401) {
      toast.error('Something went wrong', err.detail)
    }
  } else {
    toast.error('Something went wrong')
  }
}

/**
 * Global safety net for every mutation, wired into the QueryClient's
 * MutationCache in main.tsx — it fires even for mutations with their own
 * onError.
 *
 * A mutation that reports its failure itself (a conflict toast naming the
 * clashing bookings, an inline form error) opts out of the toast with
 * `meta: { errorToast: false }`; otherwise the clinician sees two errors for
 * one failure (CLA-1259). Sentry capture is never opted out of.
 */
export function reportMutationError(err: unknown, mutation: Mutation<unknown, unknown, unknown, unknown>): void {
  if (mutation.meta?.errorToast !== false) toastMutationError(err)
  captureHandledError(err, {
    source: 'react-query',
    kind: 'mutation',
    resource: String(mutation.options.mutationKey?.[0] ?? 'unknown'),
  })
}
